import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
// ARCHIVED 2026-04-21 (Bug 3-C): unmounted with Faceswap workflow
// import WorkflowPlaceholder from './pages/WorkflowPlaceholder';
import { isLoggedIn, getMe, setPaymentGrace } from './services/apiService';
import CookieBanner from './components/CookieBanner';
import AppShell from './components/AppShell';

/* ── Lazy-loaded pages (route-level code splitting) ── */
const LandingPage = React.lazy(() => import('./pages/landing/LandingPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage'));
const MainPage = React.lazy(() => import('./pages/MainPage'));
const CanvasPage = React.lazy(() => import('./pages/CanvasPage'));
// ARCHIVED 2026-04-21 (Bug 3-C): Faceswap workflow unmounted. File kept on disk for potential revival.
// const FaceswapPage = React.lazy(() => import('./pages/FaceswapPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const ForbiddenPage = React.lazy(() => import('./pages/ForbiddenPage'));

// Personal center + billing shells
const AppSettingsShell = React.lazy(() => import('./layouts/AppSettingsShell'));
const MyPlanPage = React.lazy(() => import('./pages/billing/MyPlanPage'));
const CreditsHistoryPage = React.lazy(() => import('./pages/billing/CreditsHistoryPage'));
const PaymentsPage = React.lazy(() => import('./pages/billing/PaymentsPage'));
const ReferralPage = React.lazy(() => import('./pages/billing/ReferralPage'));
const ProfilePage = React.lazy(() => import('./pages/account/ProfilePage'));
const SecurityPage = React.lazy(() => import('./pages/account/SecurityPage'));
const PreferencesPage = React.lazy(() => import('./pages/account/PreferencesPage'));
const PrivacyCenterPage = React.lazy(() => import('./pages/account/PrivacyCenterPage'));

// Help Center (guest-accessible, independent shell)
const HelpShell = React.lazy(() => import('./layouts/HelpShell'));
const HelpHomePage = React.lazy(() => import('./pages/help/HelpHomePage'));
const HelpArticlePage = React.lazy(() => import('./pages/help/HelpArticlePage'));

/** Handle legacy OAuth token and payment status from URL params */
function UrlParamHandler({ children }: { children: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token');
  const payment = searchParams.get('payment');

  useEffect(() => {
    let changed = false;
    if (token) {
      // Deprecated compatibility path. New OAuth success uses an HttpOnly
      // cookie and does not expose JWTs in the URL.
      localStorage.setItem('popwiz_token', token);
      localStorage.setItem('popwiz_user', JSON.stringify({}));
      searchParams.delete('token');
      changed = true;
    }
    if (payment) {
      // Bug 6.U1: WeChat Pay is async — the user can land here before the webhook
      // settles credits and before Stripe issues a fresh JWT. Mark a 30s grace
      // window so a transient 401 from Header.getMe doesn't immediately bounce
      // them to /login.
      if (payment === 'success') setPaymentGrace();
      // Don't delete `payment` or `session_id` — MyPlanPage needs both to poll
      // /billing/verify-session. It cleans the URL itself once verified.
    }
    if (changed) {
      setSearchParams(searchParams, { replace: true });
    }
  }, [token, payment, searchParams, setSearchParams]);

  return <>{children}</>;
}

function cacheSessionUser(me: any) {
  try {
    localStorage.setItem('popwiz_user', JSON.stringify({
      username: me.username,
      credits: me.credits,
      subscription_credits: me.subscription_credits ?? 0,
      total_credits: me.total_credits ?? me.credits,
      email_verified: me.email_verified,
    }));
  } catch { /* localStorage may be disabled */ }
}

/** Requires an authenticated session (Bearer or cookie), without verification check. */
function RequiresLoginRoute({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'ok' | 'unauth'>(
    isLoggedIn() ? 'ok' : 'loading',
  );

  useEffect(() => {
    let alive = true;
    if (isLoggedIn()) {
      setStatus('ok');
      return;
    }
    getMe()
      .then((me) => {
        cacheSessionUser(me);
        if (alive) setStatus('ok');
      })
      .catch(() => { if (alive) setStatus('unauth'); });
    return () => { alive = false; };
  }, []);

  if (status === 'loading') return <AppShell />;
  if (status === 'unauth') return <Navigate to="/app/login" replace />;
  return <>{children}</>;
}

/** Requires JWT AND a verified email. Redirects unverified users to /app/verify-email.
 *
 * Optimistic render: if a cached `popwiz_user` + valid JWT exist we start in
 * 'ok' so MainPage's useEffects can kick off in parallel with the getMe()
 * verification. Plain gray while getMe resolves would cost ~200-500ms FCP
 * on a cold tab — the shell fills that gap. Any 401 from downstream APIs
 * still triggers logout; this gate is purely cosmetic.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const hasCachedUser = typeof localStorage !== 'undefined' && !!localStorage.getItem('popwiz_user');
  const [status, setStatus] = useState<'loading' | 'ok' | 'unverified' | 'unauth'>(
    hasCachedUser && isLoggedIn() ? 'ok' : 'loading',
  );

  useEffect(() => {
    let alive = true;
    getMe()
      .then((me) => {
        cacheSessionUser(me);
        if (alive) setStatus(me.email_verified ? 'ok' : 'unverified');
      })
      .catch(() => { if (alive) setStatus('unauth'); });
    return () => { alive = false; };
  }, []);

  if (status === 'loading') return <AppShell />;
  if (status === 'unauth') return <Navigate to="/app/login" replace />;
  if (status === 'unverified') return <Navigate to="/app/verify-email" replace />;
  // Inner Suspense boundary — lazy page chunks under a protected route fall
  // through to AppShell instead of the outer gray PageFallback.
  return <Suspense fallback={<AppShell />}>{children}</Suspense>;
}

const PageFallback = <div className="h-screen bg-surface-base" />;

export default function App() {
  return (
    <>
    <Suspense fallback={PageFallback}>
    <Routes>
      {/* Landing page (public, prerendered + lazy JS hydration) */}
      <Route path="/" element={<LandingPage />} />

      {/* App routes (under /app prefix) */}
      <Route path="/app/login" element={<LoginPage />} />
      <Route path="/app/register" element={<RegisterPage />} />
      <Route path="/app/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/app/reset-password" element={<ResetPasswordPage />} />
      <Route path="/app/admin" element={<AdminPage />} />
      <Route path="/app/privacy" element={<PrivacyPage />} />
      <Route path="/app/terms" element={<TermsPage />} />
      {/* Chinese public route mirrors (M2.2). Private /app/* routes
          do NOT get /zh/ counterparts — they're behind auth and not
          zh-SEO targets. */}
      <Route path="/zh" element={<Navigate to="/zh/" replace />} />
      <Route path="/zh/" element={<LandingPage />} />
      <Route path="/zh/app/privacy" element={<PrivacyPage />} />
      <Route path="/zh/app/terms" element={<TermsPage />} />
      <Route path="/app/forbidden" element={<ForbiddenPage />} />

      {/* Verify email — requires JWT only, not verified-email */}
      <Route path="/app/verify-email" element={<UrlParamHandler><RequiresLoginRoute><VerifyEmailPage /></RequiresLoginRoute></UrlParamHandler>} />

      {/* Main app pages (protected — requires verified email) */}
      <Route path="/app" element={<UrlParamHandler><ProtectedRoute><MainPage /></ProtectedRoute></UrlParamHandler>} />
      <Route path="/app/canvas" element={<ProtectedRoute><CanvasPage /></ProtectedRoute>} />
      {/* ARCHIVED 2026-04-21 (Bug 3-C): Faceswap / bgswap / batchgen routes unmounted. Now 404. */}
      {/* <Route path="/app/workflow/faceswap" element={<ProtectedRoute><FaceswapPage /></ProtectedRoute>} /> */}
      {/* <Route path="/app/workflow/bgswap" element={<ProtectedRoute><WorkflowPlaceholder name="批量换背景" /></ProtectedRoute>} /> */}
      {/* <Route path="/app/workflow/batchgen" element={<ProtectedRoute><WorkflowPlaceholder name="批量生成" /></ProtectedRoute>} /> */}

      {/* Personal center — /app/billing/* and /app/account/* share the shell */}
      <Route path="/app/billing" element={<ProtectedRoute><AppSettingsShell /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/billing/plan" replace />} />
        <Route path="referral" element={<ReferralPage />} />
        <Route path="plan" element={<MyPlanPage />} />
        <Route path="credits" element={<CreditsHistoryPage />} />
        <Route path="payments" element={<PaymentsPage />} />
      </Route>
      <Route path="/app/account" element={<ProtectedRoute><AppSettingsShell /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/account/profile" replace />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="preferences" element={<PreferencesPage />} />
        <Route path="privacy" element={<PrivacyCenterPage />} />
      </Route>
      {/* Legacy redirect */}
      <Route path="/app/settings" element={<Navigate to="/app/account/profile" replace />} />

      {/* Help Center — public, guest-accessible */}
      <Route path="/help" element={<HelpShell />}>
        <Route index element={<HelpHomePage />} />
        <Route path=":slug" element={<HelpArticlePage />} />
      </Route>
      <Route path="/zh/help" element={<HelpShell />}>
        <Route index element={<HelpHomePage />} />
        <Route path=":slug" element={<HelpArticlePage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
    <CookieBanner />
    </>
  );
}
